import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Title, Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell, Switch, Input, MessageStrip, FlexBox, BusyIndicator, Text, Tag } from '@ui5/webcomponents-react';
import { useAuth } from '../../store/auth';
import { api, type Pack, type PostingField } from '../../api/client';

/**
 * Sector / module packs for the current company. Turning a pack off hides its menu group for everyone here;
 * turning an industry pack on lights up its group so you can design its screens. Enablement is per company.
 */
export function PacksAdmin() {
  const qc = useQueryClient();
  const companyDb = useAuth(s => s.principal?.companyDb);
  const packs = useQuery({ queryKey: ['admin-packs'], queryFn: () => api.adminPacks() });

  const toggle = async (p: Pack, enabled: boolean) => {
    try {
      await api.setPack(p.id, enabled);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin-packs'] }),
        qc.invalidateQueries({ queryKey: ['screens'] }),       // the live menu
        qc.invalidateQueries({ queryKey: ['screens-all'] }),
      ]);
    } catch (e) { alert((e as Error).message); }
  };

  if (packs.isLoading) return <FlexBox justifyContent="Center" style={{ padding: '3rem' }}><BusyIndicator active /></FlexBox>;
  const list = packs.data?.packs ?? [];
  const section = (cat: Pack['category']) => list.filter(p => p.category === cat);

  const rows = (cat: Pack['category']) => (
    <Table overflowMode="Popin" headerRow={<TableHeaderRow>
      <TableHeaderCell minWidth="12rem" importance={2}><span>Pack</span></TableHeaderCell>
      <TableHeaderCell minWidth="10rem"><span>Covers</span></TableHeaderCell>
      <TableHeaderCell width="8rem" minWidth="5rem"><span>Screens</span></TableHeaderCell>
      <TableHeaderCell width="8rem" minWidth="6rem" importance={1}><span>Enabled</span></TableHeaderCell>
    </TableHeaderRow>}>
      {section(cat).map(p => (
        <TableRow key={p.id}>
          <TableCell><FlexBox direction="Column"><Text style={{ fontWeight: 600 }}>{p.name}</Text><Text style={{ fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)' }}>{p.description}</Text></FlexBox></TableCell>
          <TableCell><Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.8rem' }}>{p.groups.join(', ')}</Text></TableCell>
          <TableCell>{p.screenCount > 0 ? <Tag colorScheme="6">{p.screenCount}</Tag> : <Text style={{ color: 'var(--sapNeutralTextColor)' }}>—</Text>}</TableCell>
          <TableCell>{p.locked
            ? <Tag colorScheme="8">Always on</Tag>
            : <Switch checked={p.enabled} onChange={e => toggle(p, e.target.checked)} />}</TableCell>
        </TableRow>
      ))}
    </Table>
  );

  return (
    <div>
      <Title level="H3" style={{ marginBottom: '0.5rem' }}>Packs — {companyDb}</Title>
      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '0.75rem' }}>
        Enable only what this company needs. Turning a pack off hides its menu group for every user here; enabling an
        industry pack lights up its group so you can design its screens. Setup &amp; Administration are always on.
      </MessageStrip>

      <Title level="H5" style={{ margin: '1rem 0 0.4rem' }}>Core modules</Title>
      {rows('Core')}

      <Title level="H5" style={{ margin: '1.5rem 0 0.4rem' }}>Industry packs</Title>
      <Text style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--sapNeutralTextColor)' }}>
        Sector flows (oil-palm, construction, F&amp;B, pharma…). Enable one, then design its screens under the matching menu group.
      </Text>
      {rows('Industry')}

      <PostingConfig />
    </div>
  );
}

/** Per-company posting configuration used by the sector posting actions (mill/refinery run, progress claim). */
function PostingConfig() {
  const cfg = useQuery({ queryKey: ['posting-config'], queryFn: api.postingConfig });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => { if (cfg.data) setDraft(Object.fromEntries(cfg.data.fields.map(f => [f.key, f.value]))); }, [cfg.data]);

  const save = async (f: PostingField) => {
    const value = draft[f.key] ?? '';
    if (value === f.value) return;
    try { await api.setPostingConfig(f.key, value); setSaved(f.key); setTimeout(() => setSaved(null), 1200); }
    catch (e) { alert((e as Error).message); }
  };

  const fields = cfg.data?.fields ?? [];
  // Derived from what the server actually returns, not hardcoded: a hardcoded list silently dropped every
  // field in a newly added group (the budgeting settings were invisible, so the fiscal year could not be set).
  const groups = [...new Set(fields.map(f => f.group))];

  return (
    <div style={{ marginTop: '2rem' }}>
      <Title level="H5" style={{ marginBottom: '0.4rem' }}>Posting configuration</Title>
      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '0.75rem' }}>
        Where the sector posting actions (mill/refinery run, progress claim) post in this company — its warehouse,
        GL accounts and co-product sales prices — plus the fiscal calendar used by budgeting. Blank uses the
        default shown. Set these per company before posting, and set the fiscal year start before budgeting.
      </MessageStrip>
      {groups.map(g => (
        <div key={g} style={{ marginBottom: '0.75rem' }}>
          <Text style={{ display: 'block', fontWeight: 600, margin: '0.5rem 0 0.25rem' }}>{g}</Text>
          <FlexBox wrap="Wrap" style={{ gap: '0.75rem' }}>
            {fields.filter(f => f.group === g).map(f => (
              <FlexBox key={f.key} direction="Column" style={{ width: '15rem' }}>
                <Text style={{ fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)' }}>{f.label}</Text>
                <Input value={draft[f.key] ?? ''} placeholder={`default ${f.default}`} valueState={saved === f.key ? 'Positive' : 'None'}
                  onInput={e => setDraft(d => ({ ...d, [f.key]: e.target.value ?? '' }))} onChange={() => save(f)} style={{ width: '100%' }} />
              </FlexBox>
            ))}
          </FlexBox>
        </div>
      ))}
    </div>
  );
}
